import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Send, Users } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { emailApi } from "../api";
import { marketingContent, emailSignature } from "../mock";

const BulkEmailModal = ({ open, onOpenChange, leads, onSent }) => {
  const { toast } = useToast();
  const withEmail = leads.filter(
    (l) => l.email || (l.contact && l.contact.includes("@"))
  );
  const [subject, setSubject] = useState(marketingContent.outreach[0].subject);
  const [body, setBody] = useState(`${marketingContent.outreach[0].body}\n\n${emailSignature}`);
  const [sending, setSending] = useState(false);

  const applyTemplate = (o) => {
    setSubject(o.subject);
    setBody(`${o.body}\n\n${emailSignature}`);
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await emailApi.sendBulk({
        lead_ids: withEmail.map((l) => l.id),
        subject,
        body,
      });
      const { sent, skipped, failed } = res.data;
      toast({
        title: `Sent to ${sent} contact${sent === 1 ? "" : "s"}`,
        description: `${failed} failed, ${skipped} skipped (no email).`,
      });
      onSent && onSent();
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Bulk send failed. Check your Resend setup.";
      toast({ title: "Send failed", description: msg, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Users className="h-5 w-5 text-[#0E5A72]" /> Email the whole list
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-[#EAF4F7] text-[#0E5A72] text-sm px-4 py-3">
            This will email <span className="font-semibold">{withEmail.length}</span> contact
            {withEmail.length === 1 ? "" : "s"} that have an email address (out of {leads.length} shown).
            Use <code className="bg-white px-1 rounded">[Name]</code> to personalize.
          </div>
          <div className="flex flex-wrap gap-2">
            {marketingContent.outreach.map((o) => (
              <Button key={o.id} size="sm" variant="outline" onClick={() => applyTemplate(o)}>
                {o.audience.split(" ")[0]}
              </Button>
            ))}
          </div>
          <div>
            <Label>Subject</Label>
            <Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label>Message</Label>
            <Textarea className="mt-1" rows={9} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <Button
            onClick={send}
            disabled={sending || withEmail.length === 0}
            className="w-full bg-[#2E9E6A] hover:bg-[#23875A] text-white"
          >
            <Send className="mr-2 h-4 w-4" />
            {sending ? "Sending..." : `Send to ${withEmail.length} contacts`}
          </Button>
          <p className="text-xs text-slate-400 text-center">
            Best-effort send via Resend. In test mode Resend only delivers to your verified address until the myohcare.com domain is verified.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkEmailModal;
